import {
  APP_STATE_TYPE,
  IAppState,
} from "modules/providers/index-db-provider/index-db-models";
import { useIndexDb } from "modules/providers/index-db-provider";
import { useLiveQuery } from "dexie-react-hooks";
import { useCallback, useEffect, useMemo } from "react";

interface IProps {
  appStateType: APP_STATE_TYPE;
  defaultValue?: {
    booleanValue?: boolean;
    stringValue?: string;
    arrayValue?: string[];
    numberValue?: number;
  };
}

const useAppState = ({ appStateType, defaultValue }: IProps) => {
  const { db } = useIndexDb();

  const appState = useLiveQuery(
    () => db.appState.where("id").equals(appStateType).first(),
    [db, appStateType],
    //if db isn't in the dependency array, this doesn't work
  );

  useEffect(() => {
    const checkAppState = async () => {
      const state = await db.appState.get(appStateType);
      if (state) {
        return;
      }
      const newAppState: IAppState = {
        id: appStateType,
        ...defaultValue,
      };
      db.appState.put(newAppState);
    };
    checkAppState();
  }, [appState, appStateType, db.appState, defaultValue]);

  const booleanValue = useMemo(
    () => appState?.booleanValue ?? defaultValue?.booleanValue ?? false,
    [appState?.booleanValue, defaultValue?.booleanValue],
  );

  const arrayValue = useMemo(
    () => appState?.arrayValue ?? defaultValue?.arrayValue ?? [],
    [appState?.arrayValue, defaultValue?.arrayValue],
  );

  const stringValue = useMemo(
    () => appState?.stringValue ?? defaultValue?.stringValue ?? "",
    [appState?.stringValue, defaultValue?.stringValue],
  );

  const numberValue = useMemo(
    () => appState?.numberValue ?? defaultValue?.numberValue ?? 0,
    [appState?.numberValue, defaultValue?.numberValue],
  );

  const deleteValue = useCallback(() => {
    db.appState.where("id").equals(appStateType).delete();
  }, [appStateType, db.appState]);

  const updateBoolean = useCallback(
    (value: boolean) => {
      db.appState
        .where("id")
        .equals(appStateType)
        .modify((appState) => {
          appState.booleanValue = value;
        });
    },
    [appStateType, db.appState],
  );

  const updateString = useCallback(
    (value: string) => {
      db.appState
        .where("id")
        .equals(appStateType)
        .modify((appState) => {
          appState.stringValue = value;
        });
    },
    [appStateType, db.appState],
  );

  const updateArray = useCallback(
    (value: string[]) => {
      db.appState
        .where("id")
        .equals(appStateType)
        .modify((appState) => {
          appState.arrayValue = value;
        });
    },
    [appStateType, db.appState],
  );

  const updateNumber = useCallback(
    (value: number) => {
      db.appState
        .where("id")
        .equals(appStateType)
        .modify((appState) => {
          appState.numberValue = value;
        });
    },
    [appStateType, db.appState],
  );

  //if exists, remove, else add
  const toggleArrayValue = useCallback(
    (value: string) => {
      db.appState
        .where("id")
        .equals(appStateType)
        .modify((appState) => {
          if (appState.arrayValue?.includes(value)) {
            appState.arrayValue = appState.arrayValue?.filter(
              (val) => val !== value,
            );
          } else {
            appState.arrayValue = [...(appState.arrayValue ?? []), value];
          }
        });
    },
    [appStateType, db.appState],
  );

  return {
    booleanValue,
    stringValue,
    numberValue,
    arrayValue,
    updateBoolean,
    updateString,
    updateNumber,
    updateArray,
    toggleArrayValue,
    deleteValue,
  };
};

export default useAppState;
